import { type RefObject, useEffect, useRef, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router'

/**
 * After an in-app navigation, move focus to the main landmark so keyboard and screen-reader
 * users start at the new page instead of wherever the old link was (WCAG 2.4.3). The first
 * render is skipped so the initial page load behaves like any other site.
 */
export function useRouteFocus(main: RefObject<HTMLElement | null>) {
  const { pathname } = useLocation()
  const first = useRef(true)
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run on every pathname change; the ref itself is stable
  useEffect(() => {
    if (first.current) {
      first.current = false
      return
    }
    if (window.location.hash) return
    const el = main.current
    if (!el) return
    el.focus({ preventScroll: true })
    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [pathname])
}

export function useDebounced<T>(value: T, delay = 250): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), delay)
    return () => window.clearTimeout(t)
  }, [value, delay])
  return v
}

/** URL-backed state for list filters so links are shareable and back/forward work. */
export function useQueryState(key: string, fallback = ''): [string, (v: string) => void] {
  const [params, setParams] = useSearchParams()
  const value = params.get(key) ?? fallback
  const set = (v: string) => {
    const next = new URLSearchParams(params)
    if (v && v !== fallback) next.set(key, v)
    else next.delete(key)
    if (key !== 'page') next.delete('page')
    setParams(next, { replace: true })
  }
  return [value, set]
}

export function useDocumentTitle(title: string) {
  useEffect(() => {
    const prev = document.title
    document.title = title ? `${title} · BTown Pride` : 'BTown Pride'
    return () => {
      document.title = prev
    }
  }, [title])
}
